import React, { useEffect } from 'react';
import { 
  Building2, 
  ArrowRight, 
  List,
  AlertCircle,
  Shield,
  BarChart3,
  CheckCircle,
  PlusCircle
} from 'lucide-react';

const PlanLimitMessage = ({ planInfo, onUpgrade, onManage }) => {
  // Información de depuración mejorada
  useEffect(() => {
    console.log('PlanLimitMessage - planInfo recibida:', planInfo);
    
    // Verificar específicamente si el plan es 'casual'
    if (planInfo && typeof planInfo.plan === 'string') {
      console.log('Plan normalizado:', planInfo.plan.toLowerCase());
    }
  }, [planInfo]);

  // Mapeo de nombres de planes con colores y badges
  const planConfig = {
    gratuito: { 
      name: 'Gratuito', 
      color: 'bg-gray-100 text-gray-700',
      badge: 'bg-gray-100 border-gray-200',
      icon: Building2
    },
    casual: { 
      name: 'Casual', 
      color: 'bg-blue-100 text-blue-700',
      badge: 'bg-blue-50 border-blue-200',
      icon: Shield
    },
    basico: { 
      name: 'Básico', 
      color: 'bg-green-100 text-green-700',
      badge: 'bg-green-50 border-green-200',
      icon: Shield
    },
    estandar: { 
      name: 'Estándar', 
      color: 'bg-purple-100 text-purple-700',
      badge: 'bg-purple-50 border-purple-200',
      icon: BarChart3
    },
    premium: { 
      name: 'Premium', 
      color: 'bg-amber-100 text-amber-700',
      badge: 'bg-amber-50 border-amber-200',
      icon: CheckCircle
    }
  };

  // Definir límites de plan
  const planLimits = {
    1: { name: 'Cliente', limit: 0, message: 'Como cliente, no puedes publicar propiedades. Actualiza a un plan de vendedor.' },
    2: {
      gratuito: { limit: 1, message: 'Tu plan Gratuito te permite una publicación.' },
      casual: { limit: 5, message: 'Tu plan Casual te permite hasta 5 publicaciones activas.' }
    },
    3: { // Constructora - todos los planes
      gratuito: { limit: 'Ilimitadas', message: 'Como constructora, tienes publicaciones ilimitadas.' },
      casual: { limit: 'Ilimitadas', message: 'Como constructora, tienes publicaciones ilimitadas.' },
      basico: { limit: 'Ilimitadas', message: 'Como constructora, tienes publicaciones ilimitadas.' },
      estandar: { limit: 'Ilimitadas', message: 'Como constructora, tienes publicaciones ilimitadas.' },
      premium: { limit: 'Ilimitadas', message: 'Como constructora, tienes publicaciones ilimitadas.' }
    },
    4: {
      basico: { limit: 10, message: 'Tu plan Básico te permite hasta 10 publicaciones activas.' },
      estandar: { limit: 20, message: 'Tu plan Estándar te permite hasta 20 publicaciones activas.' },
      premium: { limit: 'Ilimitadas', message: 'Tu plan Premium te permite publicaciones ilimitadas.' }
    }
  };

  // Manejar caso donde planInfo es null o undefined
  if (!planInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error al cargar información</h2>
            <p className="text-gray-600 mb-6">
              No se pudo obtener la información de tu cuenta. Por favor, intenta de nuevo más tarde.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-50 text-blue-600 px-6 py-3 rounded-xl font-medium 
                       hover:bg-blue-100 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // MODIFICACIÓN CRÍTICA: Normalizar el formato del plan y tipo de usuario
  const userType = typeof planInfo.typeId === 'number' ? planInfo.typeId : parseInt(planInfo.typeId || '2');
  
  // Normalizar el nombre del plan - convertir a minúsculas y manejar valores null/undefined
  let currentPlan = 'gratuito'; // valor por defecto
  if (planInfo.plan) {
    // Normalizar a minúsculas y eliminar espacios
    currentPlan = String(planInfo.plan).toLowerCase().trim();
  }
  
  // MODIFICACIÓN: Compatibilidad con diferentes formatos de datos
  // Verificar si los datos vienen del formato usePlanValidation o del formato del API
  const totalProperties = 
    typeof planInfo.totalProperties === 'number' ? 
      planInfo.totalProperties : 
    typeof planInfo.total_propiedades === 'number' ? 
      planInfo.total_propiedades : 0;
  
  // Inicializar con valores por defecto
  let limitInfo = { 
    limit: 0, 
    message: 'No tienes permiso para publicar propiedades.' 
  };

  // Obtener información específica del plan
  if (userType === 1) {
    limitInfo = planLimits[1];
  } else if (userType === 2) {
    // MODIFICACIÓN: Verificar específicamente si el plan es 'casual'
    if (currentPlan === 'casual' && planLimits[2].casual) {
      limitInfo = planLimits[2].casual;
      console.log('Utilizando límites de Plan Casual para vendedor');
    } else {
      limitInfo = planLimits[2][currentPlan] || planLimits[2].gratuito;
    }
  } else if (userType === 3) {
    limitInfo = planLimits[3][currentPlan] || planLimits[3].gratuito;
  } else if (userType === 4) {
    if (currentPlan && planLimits[4][currentPlan]) {
      limitInfo = planLimits[4][currentPlan];
    } else {
      limitInfo = planLimits[4].basico;
    }
  }

  // Obtener configuración del plan actual
  const planSettings = planConfig[currentPlan] || planConfig.gratuito;
  const PlanIcon = planSettings.icon;
  
  // Calcular porcentaje de uso para la barra de progreso
  const limit = limitInfo.limit === 'Ilimitadas' ? 100 : limitInfo.limit;
  const usagePercentage = limit ? Math.min((totalProperties / limit) * 100, 100) : 100;

  // Depuración adicional
  console.log('PlanLimitMessage - Plan procesado:', {
    userType,
    currentPlan,
    totalProperties,
    limit,
    usagePercentage
  });

  // Determinar color de la barra de progreso
  let progressColor = 'bg-blue-500';
  if (usagePercentage > 90) {
    progressColor = 'bg-red-500';
  } else if (usagePercentage > 70) {
    progressColor = 'bg-amber-500';
  } else if (usagePercentage > 50) {
    progressColor = 'bg-green-500';
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
        {/* Header */}
        <div className={`p-6 ${planSettings.color} bg-opacity-20`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Límite de Plan</h2>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${planSettings.badge} border`}>
              Plan {planSettings.name}
            </div>
          </div>
          
          <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full ${planSettings.color.split(' ')[0]} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
              <PlanIcon className={`w-6 h-6 ${planSettings.color.split(' ')[1]}`} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Estado actual</p>
              <p className="font-semibold text-gray-800 text-lg">
                {totalProperties} de {limitInfo.limit} publicaciones
              </p>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="px-6 pt-0">
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${progressColor} rounded-full`} 
              style={{ width: `${usagePercentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            {usagePercentage >= 100 
              ? 'Límite alcanzado' 
              : `${Math.round(usagePercentage)}% utilizado`}
          </p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
            <p className="text-gray-700">
              {userType === 1 
                ? limitInfo.message 
                : `${limitInfo.message} Actualmente estás utilizando ${totalProperties} publicaciones.`}
            </p>
          </div>
          
          {/* Buttons */}
          <div className="space-y-3">
            {userType === 1 ? (
              <button
                onClick={onUpgrade}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl 
                         font-medium flex items-center justify-center gap-2
                         hover:bg-blue-700 transition-all shadow-sm"
              >
                <PlusCircle className="w-5 h-5" />
                Actualizar a Vendedor
              </button>
            ) : (
              <>
                <button
                  onClick={onUpgrade}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl 
                           font-medium flex items-center justify-center gap-2
                           hover:bg-blue-700 transition-all shadow-sm"
                >
                  Subir de Plan
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={onManage}
                  className="w-full bg-white text-gray-700 px-6 py-3 rounded-xl
                           font-medium flex items-center justify-center gap-2
                           hover:bg-gray-50 transition-all border border-gray-200"
                >
                  Administrar Propiedades
                  <List className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-gray-500">
            Si necesitas ayuda con tu plan, contacta a nuestro 
            <button className="text-blue-600 underline ml-1">
              servicio de soporte
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlanLimitMessage;